import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { validateAwb, formatAwbOnInput, parseAwb } from '../../utils/awb';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Flight as FlightIcon,
  BookmarkBorder as BookingIcon,
  CheckCircle as ConfirmedIcon,
  Schedule as StandbyIcon,
  Cancel as CancelledIcon,
  PersonOff as NoShowIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const FlightBookings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Vuelos disponibles
  const [vuelos, setVuelos] = useState([]);
  const [selectedFlight, setSelectedFlight] = useState('');

  // Capacidad del vuelo seleccionado
  const [capacity, setCapacity] = useState(null);

  // Bookings del vuelo
  const [bookings, setBookings] = useState([]);

  // Estaciones para selects
  const [estaciones, setEstaciones] = useState([]);

  // Agentes para select
  const [agentes, setAgentes] = useState([]);

  // Modal de booking
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [formData, setFormData] = useState({
    awb_number: '',
    origin: '',
    destination: '',
    pieces: 0,
    weight: '',
    agent_id: '',
    notes: '',
    status: 'confirmed',
  });

  useEffect(() => {
    fetchVuelos();
    fetchEstaciones();
    fetchAgentes();
  }, []);

  useEffect(() => {
    if (selectedFlight) {
      fetchBookings();
    } else {
      setBookings([]);
      setCapacity(null);
    }
  }, [selectedFlight]);

  const fetchVuelos = async () => {
    try {
      const res = await api.get(`${endpoints.vuelos.list}?cerrado=0`);
      setVuelos(res.data.data?.vuelos || []);
    } catch (error) {
      console.error('Error cargando vuelos:', error);
    }
  };

  const fetchEstaciones = async () => {
    try {
      const res = await api.get(endpoints.estaciones);
      const data = res.data.data?.data || res.data.data || [];
      setEstaciones(data);
    } catch (error) {
      console.error('Error cargando estaciones:', error);
    }
  };

  const fetchAgentes = async () => {
    try {
      const res = await api.get(endpoints.oficina.agentes);
      setAgentes(res.data.data || []);
    } catch (error) {
      console.error('Error cargando agentes:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const vuelo = vuelos.find(v => v.awb === selectedFlight);
      if (!vuelo) return;

      const res = await api.get(`${endpoints.bookings.list}?flight_id=${vuelo.id}`);
      setBookings(res.data.data.bookings || []);
      setCapacity(res.data.data.capacity || null);
    } catch (error) {
      console.error('Error cargando bookings:', error);
      setError('Error al cargar bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode, booking = null) => {
    setDialogMode(mode);
    setSelectedBooking(booking);

    if (booking) {
      setFormData({
        awb_number: booking.awb_number || '',
        origin: booking.origin || '',
        destination: booking.destination || '',
        pieces: booking.pieces || 0,
        weight: booking.weight || '',
        agent_id: booking.agent_id || '',
        notes: booking.notes || '',
        status: booking.status || 'confirmed',
      });
    } else {
      // Valores por defecto
      const vuelo = vuelos.find(v => v.awb === selectedFlight);
      const defaultDestination = vuelo?.destino?.split(';')[0] || '';

      setFormData({
        awb_number: '',
        origin: vuelo?.origen || '',
        destination: defaultDestination,
        pieces: 0,
        weight: '',
        agent_id: '',
        notes: '',
        status: 'confirmed',
      });
    }

    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBooking(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Formatear AWB automáticamente (123-45678900)
    if (name === 'awb_number') {
      processedValue = formatAwbOnInput(value);
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.awb_number || !formData.origin || !formData.destination || !formData.weight) {
      setError('Campos requeridos: AWB, Origen, Destino, Peso');
      return;
    }
    
    // Validar formato AWB (11 dígitos)
    if (!validateAwb(formData.awb_number)) {
      setError('AWB inválido. Debe tener 11 dígitos (formato: 123-45678900)');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const vuelo = vuelos.find(v => v.awb === selectedFlight);

      if (dialogMode === 'create') {
        const payload = {
          flight_id: vuelo.id,
          ...formData,
          awb_number: parseAwb(formData.awb_number), // Enviar sin guion
          pieces: parseInt(formData.pieces) || 0,
          weight: parseFloat(formData.weight),
        };

        const res = await api.post(endpoints.bookings.list, payload);

        if (res.data.warning) {
          setSuccess(`${res.data.data.message}. AVISO: ${res.data.data.warning}`);
        } else {
          setSuccess('Booking creado exitosamente');
        }
      } else {
        await api.put(endpoints.bookings.list, {
          id: selectedBooking.id,
          ...formData,
          pieces: parseInt(formData.pieces) || 0,
          weight: parseFloat(formData.weight),
        });
        setSuccess('Booking actualizado exitosamente');
      }

      handleCloseDialog();
      fetchBookings();
      setTimeout(() => setSuccess(''), 4000);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar booking');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await api.put(endpoints.bookings.list, {
        id: bookingId,
        status: newStatus,
      });
      fetchBookings();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al cambiar status');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!confirm('¿Cancelar este booking?')) return;

    try {
      await api.delete(endpoints.bookings.list, { data: { id: bookingId } });
      fetchBookings();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al cancelar booking');
    }
  };

  const getStatusChip = (status) => {
    const configs = {
      confirmed: { label: 'Confirmado', color: 'success', icon: <ConfirmedIcon fontSize="small" /> },
      standby: { label: 'Standby', color: 'warning', icon: <StandbyIcon fontSize="small" /> },
      no_show: { label: 'No Show', color: 'error', icon: <NoShowIcon fontSize="small" /> },
      cancelled: { label: 'Cancelado', color: 'default', icon: <CancelledIcon fontSize="small" /> },
    };

    const config = configs[status] || configs.confirmed;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const getCapacityColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #7B1FA2 0%, #9C27B0 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BookingIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Reservas de Carga
                </Typography>
                <Typography sx={{ opacity: 0.9 }}>
                  Gestión de capacidad y reservas por vuelo
                </Typography>
              </Box>
            </Box>
            <Button
              component={Link}
              to="/aerolinea"
              startIcon={<BackIcon />}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
              variant="outlined"
            >
              Volver
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Selector de Vuelo */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Seleccionar Vuelo</InputLabel>
                <Select
                  value={selectedFlight}
                  onChange={(e) => setSelectedFlight(e.target.value)}
                  label="Seleccionar Vuelo"
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return <em style={{ color: '#999' }}>Seleccionar vuelo</em>;
                    const vuelo = vuelos.find(v => v.awb === value);
                    return vuelo ? `${vuelo.awb} - ${vuelo.destino} (${vuelo.fecha})` : value;
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Seleccionar vuelo</em>
                  </MenuItem>
                  {vuelos.map((v) => (
                    <MenuItem key={v.awb} value={v.awb}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FlightIcon fontSize="small" />
                        <strong>{v.awb}</strong>
                        <Chip label={v.destino} size="small" />
                        <Typography variant="caption" color="text.secondary">
                          {v.fecha} - {v.equipo_nombre}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {selectedFlight && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('create')}
                  disabled={capacity?.cerrado}
                >
                  Nueva Reserva
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Indicador de Capacidad */}
      {capacity && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Capacidad del Vuelo
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {capacity.percentage.toFixed(1)}% ocupado
                </Typography>
                <Typography variant="body2">
                  {parseFloat(capacity.reserved).toLocaleString()} / {parseFloat(capacity.maxload).toLocaleString()} kg
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(capacity.percentage, 100)}
                color={getCapacityColor(capacity.percentage)}
                sx={{ height: 12, borderRadius: 1 }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {parseFloat(capacity.maxload).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Capacidad (kg)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                  <Typography variant="h5" fontWeight="bold" color="warning.dark">
                    {parseFloat(capacity.reserved).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reservado (kg)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                  <Typography variant="h5" fontWeight="bold" color="success.dark">
                    {parseFloat(capacity.available).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Disponible (kg)
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabla de Bookings */}
      {selectedFlight && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Reservas ({bookings.length})
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : bookings.length === 0 ? (
              <Alert severity="info">
                No hay reservas para este vuelo. Haz clic en "Nueva Reserva" para crear una.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>AWB</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Ruta</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Piezas</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Peso (kg)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Agente</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id} hover>
                        <TableCell>
                          <Typography fontWeight="bold">{booking.awb_number}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={`${booking.origin} → ${booking.destination}`} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{booking.pieces}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {parseFloat(booking.weight).toLocaleString()}
                        </TableCell>
                        <TableCell>{booking.agente_nombre || '-'}</TableCell>
                        <TableCell>{getStatusChip(booking.status)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('edit', booking)}
                              disabled={booking.status === 'cancelled'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {booking.status === 'confirmed' && (
                            <Tooltip title="Marcar No-Show">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleStatusChange(booking.id, 'no_show')}
                              >
                                <NoShowIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {booking.status === 'standby' && (
                            <Tooltip title="Confirmar">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleStatusChange(booking.id, 'confirmed')}
                              >
                                <ConfirmedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Cancelar">
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => handleDelete(booking.id)}
                              disabled={booking.status === 'cancelled'}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookingIcon />
            {dialogMode === 'create' ? 'Nueva Reserva' : 'Editar Reserva'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="AWB Number"
                name="awb_number"
                value={formData.awb_number}
                onChange={handleInputChange}
                placeholder="577-12345678"
                inputProps={{ style: { textTransform: 'uppercase' } }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Status"
                  displayEmpty
                >
                  <MenuItem value="confirmed">Confirmado</MenuItem>
                  <MenuItem value="standby">Standby</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel shrink>Origen</InputLabel>
                <Select
                  name="origin"
                  value={formData.origin}
                  onChange={handleInputChange}
                  label="Origen"
                  displayEmpty
                  renderValue={(value) => value || <em style={{ color: '#999' }}>Seleccionar</em>}
                >
                  <MenuItem value="" disabled>
                    <em>Seleccionar origen</em>
                  </MenuItem>
                  {estaciones.map((e) => (
                    <MenuItem key={e.id} value={e.code}>
                      <strong>{e.code}</strong>&nbsp;- {e.Nombre || e.Pais || ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel shrink>Destino</InputLabel>
                <Select
                  name="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  label="Destino"
                  displayEmpty
                  renderValue={(value) => value || <em style={{ color: '#999' }}>Seleccionar</em>}
                >
                  <MenuItem value="" disabled>
                    <em>Seleccionar destino</em>
                  </MenuItem>
                  {estaciones.map((e) => (
                    <MenuItem key={e.id} value={e.code}>
                      <strong>{e.code}</strong>&nbsp;- {e.Nombre || e.Pais || ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Piezas"
                name="pieces"
                type="number"
                value={formData.pieces}
                onChange={handleInputChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Peso (kg)"
                name="weight"
                type="number"
                value={formData.weight}
                onChange={handleInputChange}
                required
                inputProps={{ min: 0.1, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Agente</InputLabel>
                <Select
                  name="agent_id"
                  value={formData.agent_id}
                  onChange={handleInputChange}
                  label="Agente"
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return <em style={{ color: '#999' }}>Seleccionar</em>;
                    const agente = agentes.find(a => a.id === value);
                    return agente ? agente.nombre : value;
                  }}
                >
                  <MenuItem value="">
                    <em>Sin agente</em>
                  </MenuItem>
                  {agentes.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>

            {/* Mostrar disponibilidad */}
            {capacity && dialogMode === 'create' && formData.weight && (
              <Grid item xs={12}>
                <Alert severity={parseFloat(formData.weight) <= capacity.available ? 'success' : 'warning'}>
                  {parseFloat(formData.weight) <= capacity.available
                    ? `Hay suficiente capacidad. Disponible: ${capacity.available.toLocaleString()} kg`
                    : `Capacidad excedida. Disponible: ${capacity.available.toLocaleString()} kg. Se marcará como Standby.`}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} startIcon={<CloseIcon />} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {dialogMode === 'create' ? 'Crear Reserva' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlightBookings;



